import React from 'react';
import { MapDialog, MapDialogMap } from '../../../easymap/mapDialog/MapDialog';
import { AffAircraftEntity } from './affServiceAdapter';
import { dateTimeInNwt } from '../../../utils/nwtDate';
import { AffAsOf } from './AffAsOf';
import './AffAircraftModals.scss';

interface AffAircraftSummaryModalProps {
  aircraft: AffAircraftEntity;
  onClose: () => void;
  /** Opens the detail view. Absent without aff.explore, and so is the button. */
  onMoreInfo?: () => void;
  /**
   * Anchoring is OPTIONAL (#805 axis 1). Given a map and the aircraft's
   * position the panel stays attached to the marker it describes; without one
   * — opened from the explorer, say — it is simply positioned in the viewport.
   */
  map?: MapDialogMap | null;
  anchor?: { lng: number; lat: number };
  /** Height of the marker icon, so the panel clears it by a constant gap. */
  anchorIconHeight?: number;
  /** When this view of the aircraft was taken (epoch ms). See AffAircraftModalHost. */
  asOf?: number;
}

const line = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/**
 * First level of the aircraft drill-down: who and where, at a glance.
 *
 * Deliberately map-free (#779). A duty officer clicking View on a tail number
 * wants the identity and the last known position immediately; the map costs a
 * render and a viewport change they did not ask for. Anything more — the full
 * field set and putting tracks on the map — is behind More Info.
 */
const AffAircraftSummaryModal: React.FC<AffAircraftSummaryModalProps> = ({
  aircraft,
  onClose,
  onMoreInfo,
  map,
  anchor,
  anchorIconHeight,
  asOf,
}) => (
  <MapDialog
    isOpen
    onClose={onClose}
    width={520}
    map={map}
    anchor={anchor}
    anchorIconHeight={anchorIconHeight}
    moveable
    ariaLabel={`Aircraft ${line(aircraft.name)}`}
    header={<span className="aff-modal-title">{line(aircraft.name)}</span>}
  >
    <div className="aff-summary">
      <dl className="aff-field-grid">
        <dt>Tail</dt><dd>{line(aircraft.name)}</dd>
        {/* #798 — a duty officer identifies an aircraft by its call sign as
            readily as its registration, and needs to know fixed from rotary
            wing. Both come from Selkirk and were discarded before storage
            until the collector kept them. */}
        <dt>Call Sign</dt><dd>{line((aircraft as any).callsign)}</dd>
        <dt>Type</dt><dd>{line((aircraft as any).airframe)}</dd>
        <dt>Aircraft</dt><dd>{`${line(aircraft.make)} ${line(aircraft.model)}`.trim()}</dd>
        <dt>Owner</dt><dd>{line(aircraft.owner)}</dd>
        <dt>Function</dt><dd>{line(aircraft.operationalFunction)}</dd>
        <dt>Position</dt>
        <dd>
          {aircraft.lat != null && aircraft.lon != null
            ? `${Number(aircraft.lat).toFixed(5)}, ${Number(aircraft.lon).toFixed(5)}`
            : '—'}
        </dd>
        <dt>Observed</dt><dd>{line(dateTimeInNwt(aircraft.positionTimestamp as any))}</dd>
      </dl>

      <AffAsOf asOf={asOf} />

      {aircraft.stale === true && (
        // Dimming alone is a visual cue that does not survive a screen reader,
        // so the state is stated in words as well.
        <p className="aff-stale-notice">
          AFF reports this position as stale — the aircraft has not checked in recently.
        </p>
      )}

      {onMoreInfo && (
        <div className="aff-modal-actions">
          <button type="button" className="btn btn-primary" onClick={onMoreInfo}>
            More Info
          </button>
        </div>
      )}
    </div>
  </MapDialog>
);

export default AffAircraftSummaryModal;
